import React, { useCallback, useEffect, useMemo } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTokenContext } from 'src/account/shared/service/token.context';
import {
  EVENT_DATE_PARTICIPANT_DATA,
  EVENT_SCHEDULE_SEARCH,
  eventAggregateDetailsQuery,
} from 'src/coach/graphql/event-date-participants.query';
import { Classes, CustomCalendar, Loading } from 'src/shared';
import './schedule.page.sass';
import { daysInMonth } from 'src/coach/service/coach.utils';
import { CustomDrawer } from 'src/shared/index';
import { AdminFilters } from './filters/filters';
import { format, isSameDay } from 'date-fns';
import { orderBy } from 'lodash-es';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useHistory, useLocation } from 'react-router-dom';
import { ClassDates } from 'src/shared/ui/components/classDates/classDates';
import { useSearchParams } from 'src/shared/hooks/use-search-params';

export function CoachSchedulePage({
  data: profile,
  handleTabChange,
  content = [],
  refetch: fetchProfile,
}) {
  const [params] = useSearchParams();
  const { coachTokendecoded } = useTokenContext();
  const [selectedDate, setSelectedDate] = React.useState(dayjs(params?.date));
  const [selectedMonth, setSelectedMonth] = React.useState(
    dayjs().format('YYYY-MM'),
  );
  const [filters, setFilters] = React.useState({});

  const location = useLocation();
  const history = useHistory();

  const isMySchedule = useMemo(
    () => !location.pathname.includes('full-schedule'),
    [location],
  );
  const isAdmin = useMemo(
    () => profile?.account?.roles?.some((role) => role === 4 || role === 5),
    [profile?.account?.roles],
  );

  useEffect(() => {
    if (isAdmin === undefined) return;
    if (!isAdmin && !isMySchedule) {
      history.push('/portal/schedule');
    }
  }, [isAdmin, isMySchedule, history]);

  const getStatusFilters = useCallback(() => {
    if (filters?.status?.find((el) => el.key === 'allSession')?.action) {
      return {};
    }
    return {
      unStaffed:
        filters?.status?.find((el) => el.key === 'unStaffed')?.action || false,
      zeroEnrollment:
        filters?.status?.find((el) => el.key === 'zeroEnrollment')?.action ||
        false,
      cancelled:
        filters?.status?.find((el) => el.key === 'cancelled')?.action || false,
    };
  }, [filters.status]);

  const getFromAndToDates = useMemo(() => {
    if (isMySchedule) {
      const [year, month] = selectedMonth.split('-');
      return {
        from: `${selectedMonth}-01`,
        to: `${selectedMonth}-${daysInMonth(month, year)}`,
      };
    }
    if (isAdmin) {
      return {
        startDate: format(selectedDate.toDate(), 'yyyy-MM-dd'),
        endDate: format(selectedDate.toDate(), 'yyyy-MM-dd'),
      };
    }
    return {};
  }, [isAdmin, isMySchedule, selectedDate, selectedMonth]);

  const [
    getEventAggregateDetails,
    {
      data: eventAggregateDetailsCurrentData,
      previousData: previousEventAggregateDetailsData,
    },
  ] = useLazyQuery(eventAggregateDetailsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const eventAggregateDetailsData =
    eventAggregateDetailsCurrentData?.eventAggregateDetails ||
    previousEventAggregateDetailsData?.eventAggregateDetails;

  const eventAggregateMap = useMemo(() => {
    if (!eventAggregateDetailsData?.length) return {};
    return eventAggregateDetailsData?.reduce((accumulator, item) => {
      accumulator[item.id] = item;
      return accumulator;
    }, {});
  }, [eventAggregateDetailsData]);

  const {
    data: currentData,
    previousData,
    loading,
    refetch,
  } = useQuery(EVENT_DATE_PARTICIPANT_DATA, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      const ids =
        response?.getEventDateParticipants?.participantDetail
          ?.map((e) => e?.eventDate?.id)
          .filter(Boolean) || [];
      if (ids.length === 0) return;
      getEventAggregateDetails({
        variables: {
          ids,
        },
      });
    },
    variables: {
      input: {
        franchiseIds: coachTokendecoded.franchiseIds,
        accountId: coachTokendecoded?.id,
        ...getFromAndToDates,
      },
    },
    skip: !isMySchedule,
    notifyOnNetworkStatusChange: true,
  });
  const {
    data: currentEventScheduleData,
    previousData: previousEventScheduleData,
    loading: eventScheduleLoading,
    refetch: refetchEventSchedule,
  } = useQuery(EVENT_SCHEDULE_SEARCH, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      const ids =
        response?.eventScheduleSearch?.results
          ?.map((e) => e?.id)
          .filter(Boolean) || [];
      if (ids.length === 0) return;
      getEventAggregateDetails({
        variables: {
          ids,
        },
      });
    },
    variables: {
      input: {
        locations: filters?.locations?.map((el) => el.id) || [],
        coaches: filters?.coaches?.map((el) => el.id) || [],
        ...getStatusFilters(),
        ...getFromAndToDates,
      },
    },
    skip: isMySchedule || !isAdmin,

    notifyOnNetworkStatusChange: true,
  });
  const data = currentData ?? previousData;
  const eventScheduleData =
    currentEventScheduleData ?? previousEventScheduleData;
  const filteredAndSortedEvents = useMemo(() => {
    if (!isMySchedule) {
      return [];
    }
    const allEvents = data?.getEventDateParticipants?.participantDetail || [];
    const filteredEvents = allEvents.filter((ev) =>
      isSameDay(
        selectedDate.toDate(),
        new Date(ev.eventDate?.startDateTimeInUTC),
      ),
    );
    return orderBy(filteredEvents, ['eventDate.startDateTimeInUTC'], ['asc']);
  }, [data, selectedDate, isMySchedule]);

  const addQueryParam = useCallback((value) => {
    const url = new URL(window.location.href);
    url.searchParams.set('date', value);
    window.history.pushState({}, '', url.toString());
  }, []);

  const handleDateChange = useCallback(
    (increment) => {
      setSelectedDate((prevDate) => {
        const newDate = prevDate.add(increment, 'day');
        addQueryParam(newDate.format('YYYY-MM-DD'));
        return newDate;
      });
    },
    [setSelectedDate, addQueryParam],
  );

  const handleDaySelection = useCallback(
    (date) => {
      setSelectedDate(dayjs(date));
      addQueryParam(dayjs(date).format('YYYY-MM-DD'));
    },
    [setSelectedDate, addQueryParam],
  );

  const DateSection = () => (
    <div className="flex-container announcement-heading">
      <ArrowBackIos
        className="pointer"
        fontSize="small"
        onClick={() => handleDateChange(-1)}
      />
      <DatePicker
        selected={selectedDate.toDate()}
        onChange={handleDaySelection}
        customInput={
          <div onClick={(e) => e.preventDefault()}>
            <span className="left-margin announcement-heading__title--1">
              {selectedDate.format('D MMMM')}
            </span>
            <span className="left-margin announcement-heading__title--2">
              {isMySchedule
                ? filteredAndSortedEvents.length
                : eventScheduleData?.eventScheduleSearch?.results?.length || 0}
            </span>
          </div>
        }
      />
      <ArrowForwardIos
        className="left-margin pointer"
        fontSize="small"
        onClick={() => handleDateChange(1)}
      />
    </div>
  );

  return (
    <div className="tw-pb-32" id="list-container">
      <div className="schedule-header">
        <CustomDrawer
          data={profile}
          refetch={fetchProfile}
          content={content}
          handleTabChange={handleTabChange}
        />
        <span className="centerTitle">Schedule</span>
        {isAdmin ? (
          <span className="searchIcon">
            <AdminFilters setFilters={setFilters} />
          </span>
        ) : (
          <span />
        )}
      </div>
      {isAdmin && (
        <div className="date-container">
          <div className="schedule-container">
            <div
              className="tab-items"
              style={{
                backgroundColor: isMySchedule ? 'white' : 'transparent',
              }}
              onClick={() => history.push('/portal/schedule')}
            >
              <span>MY SCHEDULE</span>
            </div>
            <div
              className="tab-items"
              style={{
                backgroundColor: isMySchedule ? 'transparent' : 'white',
              }}
              onClick={() => history.push('/portal/full-schedule')}
            >
              <span>FULL SCHEDULE</span>
            </div>
          </div>
          {!isMySchedule && <DateSection />}
        </div>
      )}
      {isMySchedule && (
        <CustomCalendar
          classes={data?.getEventDateParticipants?.participantDetail || []}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          setSelectedMonth={setSelectedMonth}
        />
      )}
      {isMySchedule && <DateSection />}
      {(loading || eventScheduleLoading) && (
        <Loading className="component--loading flex flow-column align-center justify-center relative-loader" />
      )}
      {isMySchedule ? (
        <Classes
          classes={filteredAndSortedEvents}
          refetch={refetch}
          eventAggregateMap={eventAggregateMap}
        />
      ) : (
        <ClassDates
          classes={eventScheduleData?.eventScheduleSearch?.results || []}
          refetch={refetchEventSchedule}
          profile={profile?.account}
          eventAggregateMap={eventAggregateMap}
        />
      )}
      {!(loading || eventScheduleLoading) &&
        (isMySchedule
          ? filteredAndSortedEvents.length === 0
          : eventScheduleData?.eventScheduleSearch?.results?.length === 0) && (
          <div className="ma-12 mt-36 flex justify-center">
            <span className="fw-600">No Events Scheduled!</span>
          </div>
        )}
    </div>
  );
}
